import React, { FC, useState, useCallback, useContext, useMemo } from "react"
import { EmailValidationWrapper, HeaderGif, Message, Footer, ResendLink, Email, StyledButton, StyledReactCodeInput, LoaderContainer, Error } from "./email-validation.styles"
import { Title } from "@giphy/react-giphy-brand"
import { verifyEmail } from "../../api"
import SnakeLoader from "../snake-loader"
import { FlashMessageContext } from "../flash-message/flash-message"

type EmailValidationProps = {
    user: {
        email: string,
        username: string
    },
    setEmailConfirmed: (string) => void,
}

const EmailValidation: FC<EmailValidationProps> = ({ user: { email }, setEmailConfirmed }) => {
    const [code, setCode] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(false)

    const { showMessage } = useContext(FlashMessageContext)

    const codeChangedCallback = (value) => {
        setCode(value)
        setError(false)
    }

    const submitCode = useCallback(() => {
        setSubmitting(true)

        const submit = async () => {
            try {
                await verifyEmail({
                    email,
                    code,
                })
                showMessage({ slug: 'registration_confirm_success' })
                setEmailConfirmed(true)
            } catch (err) {
                if (err.response && err.response.status === 410) {
                    showMessage({ slug: 'registration_confirm_already_confirmed' })
                    setEmailConfirmed(true)
                } else {
                    setError(true)
                }
            } finally {
                setSubmitting(false)
            }
        }
        submit()
    }, [code])

    const input = useMemo(() => {
        // workaround for poor TS typing within the library
        // @ts-ignore
        return <StyledReactCodeInput fields={4} type="text" onChange={codeChangedCallback} value={code} error={error}/>
    }, [code, error])

    return <EmailValidationWrapper>
        <HeaderGif src="https://media2.giphy.com/media/LOnt6uqjD9OexmQJRB/200w.gif" width={164} />
        <Title>Check your Email!</Title>
        <Message>We have sent an email to <Email>{email || 'test@email.com'}</Email>. Please click the link in the email to verify your account or enter the email activation code below.</Message>
        {input}
        {error && <Error>Invalid Code</Error>}
        <Footer>Didn’t recieve an email? Check your spam folder! <br />Emails may take a couple minutes to arrive.</Footer>
        <Footer>Already validated your account?!</Footer>
        <ResendLink onClick={() => { setEmailConfirmed(true) }}>Go to Log In</ResendLink>
        <StyledButton disabled={code.length < 4} onClick={submitCode}>
            { submitting ? 'Confirming...' : 'Submit' }
            { submitting && <LoaderContainer>
                <SnakeLoader />
            </LoaderContainer>}
        </StyledButton>
    </EmailValidationWrapper>
}

export default EmailValidation
